import { FormDefinitionType, Patient, ScreeningForm } from "@/api/dtos";
import { createKeysMap } from "@/utils/typeUtils";
import { Chip, Skeleton, styled } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { IInstitution, useFormLookups, useFormMetadata, useLookups } from "@ngt/opms";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const screeningFormKeys = createKeysMap<ScreeningForm>();

const useScreenFailTableColumns = ( 
    institutions: IInstitution[] | null,
    screeningForms: ScreeningForm[] | undefined,
    loading: boolean
): GridColDef<Patient>[] => {

    const screeningFormMetadata = useFormMetadata(FormDefinitionType.ScreeningForm); // screening form
    const { data: screeningFormLookups } = useLookups(screeningFormMetadata);
    const screeningFormLookupValues = useFormLookups(screeningFormLookups);
    const screenFailReasons = screeningFormLookupValues?.find(x => x.propertyName == screeningFormKeys.screenFailReason!);

    const getReasonForFailLabel = useCallback((reasonTypeId: number | null | undefined) => {
        const reasonLookupItem = screenFailReasons?.items.find(x => x.id == reasonTypeId);

        return reasonLookupItem?.value ?? "Unknown";
    }, [screenFailReasons]);

    const reasonByPatientId = useMemo(() => {
        const map = new Map<number, number | undefined>();

        screeningForms?.forEach(form => {
            if (form.patientId == null) {
                return;
            }

            // there are multiple screening forms for a patient.
            if (form.screenFailReason != null || !map.has(form.patientId)) {
                map.set(form.patientId, form.screenFailReason);
            }
        });

        return map;
    }, [screeningForms]);

    const columns = useMemo<GridColDef<Patient>[]>(() => [
        {
            field: 'studyNumber',
            headerName: 'Temp. ID',
            width: 140
        },
        {
            field: 'institutionCode',
            headerName: 'Site Code',
            renderCell: rowData => (
                <StyledChip
                    label={rowData.row.institutionCode}
                    size="small"
                />
            ),
            width: 110
        },
        {
            field: 'institutionId',
            headerName: 'Site Name',
            valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
            flex: 1
        },
        {
            field: 'enteredDate',
            headerName: 'Date Entered',
            renderCell: ({row}) => (
                row.enteredDate ? DateTime.fromISO(row.enteredDate).toFormat('dd/MM/yyyy') : null
            ),
            width: 150
        },
        {
            field: 'screenFailReason',
            headerName: 'Reason',
            filterable: false,
            // The reason lives on the screening form, not the patient row
            valueGetter: (_value, row) => row.id != null ? reasonByPatientId.get(row.id) : undefined,
            renderCell: rowData => (
                loading ? <Skeleton /> : getReasonForFailLabel(rowData.value)
            ),
            sortComparator: (a, b) => getReasonForFailLabel(a).localeCompare(getReasonForFailLabel(b)),
            flex: 1
        },
    ], [institutions, loading, reasonByPatientId, getReasonForFailLabel]);

    return columns;
}

export default useScreenFailTableColumns;
